import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { useEffect, useState } from 'react';
import { useGetAppuntamentiMeseQuery, useGetSediVisibiliQuery } from '../../store/services/CalendarHoursDaysService';
import AppointmentsDayDialog from '../../components/main/AppointmentsDayDialog/AppointmentsDayDialog';
import BreadCrumbsAppoUser from '../../components/main/BreadcrumbsAppoUser';
import Loader from '../../components/main/Loader';
import { skipToken } from '@reduxjs/toolkit/query';

type DayInfo = {
  count: number;
  limiteRaggiunto: boolean;
};

export default function AppointmentsCalendar() {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedSedeIndex, setSelectedSedeIndex] = useState(0);
  const [selectedDay, setSelectedDay] = useState<any>(null);
  const [showModal, setShowModal] = useState(false);
  const [appointmentMap, setAppointmentMap] = useState<Record<string, DayInfo>>({});

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth() + 1;

  const sediDefinizioni = [
    { id: 0, nome: 'Milano', chiave: 'milano', regione: 'Lombardia' },
    { id: 1, nome: 'Bologna', chiave: 'bologna', regione: 'Emilia Romagna' },
    { id: 2, nome: 'Napoli', chiave: 'napoli', regione: 'Campania' },
    { id: 3, nome: 'Bari', chiave: 'bari', regione: 'Puglia' }
  ];

  // 1. Chiamata per ottenere le sedi visibili
  const { data: sediVisibiliData, isLoading: sediLoading } = useGetSediVisibiliQuery('');

  // Filtra le sedi in base alla response (solo quelle con valore true)
  const sedi = sediDefinizioni.filter(sede => sediVisibiliData?.result?.[sede.chiave] === true);
  const selectedSede = sedi[selectedSedeIndex];

  // 2. Chiamata per ottenere il count mensile della sede selezionata
  const { data: meseData, isLoading: meseLoading } = useGetAppuntamentiMeseQuery(selectedSede?.nome ? {
    anno: year,
    mese: month,
    sedeChiave: selectedSede?.nome
  } : skipToken);

  useEffect(() => {
    if (!meseData?.result) {
      console.log('Nessun result in meseData');
      return;
    }
    
    const map: Record<string, DayInfo> = {};

    meseData.result.appuntamenti.forEach((giorno: any) => {
      map[giorno.data] = {
        count: giorno.countGiornalieri,
        limiteRaggiunto: giorno.limiteRaggiunto
      };
    });

    setAppointmentMap(map);

  }, [meseData]);

  // Cambio sede: reset alla prima sede visibile se quella corrente non è più valida
  useEffect(() => {
    if (sedi.length > 0 && !sedi[selectedSedeIndex]) {
      setSelectedSedeIndex(0);
    }
  }, [sedi, selectedSedeIndex]);

  const getDaysInMonth = (date: Date) =>
    new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();

  const getFirstDayOfMonth = (date: Date) =>
    new Date(date.getFullYear(), date.getMonth(), 1).getDay();

  // const isDatePassed = (day: number) => {
  //   const dateToCheck = new Date(
  //     currentDate.getFullYear(),
  //     currentDate.getMonth(),
  //     day
  //   );
  //   const today = new Date();
  //   today.setHours(0, 0, 0, 0);
  //   return dateToCheck < today;
  // };

  const handlePrevMonth = () =>
    setCurrentDate(
      new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1)
    );

  const handleNextMonth = () =>
    setCurrentDate(
      new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1)
    );

  const handleDayClick = (day: number) => {
    const dateStr = `${currentDate.getFullYear()}-${String(
      currentDate.getMonth() + 1
    ).padStart(2, '0')}-${String(day).padStart(2, '0')}`;

    setSelectedDay({ day, dateStr });
    setShowModal(true);
  };

  const renderCalendar = () => {
    const daysInMonth = getDaysInMonth(currentDate);
    const firstDay = getFirstDayOfMonth(currentDate);
    const days = [];

    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className="p-2" />);
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = `${currentDate.getFullYear()}-${String(
        currentDate.getMonth() + 1
      ).padStart(2, '0')}-${String(day).padStart(2, '0')}`;

      const isToday =
        new Date().toDateString() ===
        new Date(
          currentDate.getFullYear(),
          currentDate.getMonth(),
          day
        ).toDateString();

      const passed = false;
      const dayInfo = appointmentMap[dateStr];
      const count = dayInfo?.count || 0;
      const limiteRaggiunto = dayInfo?.limiteRaggiunto || false;

      days.push(
        <div
          key={day}
          onClick={() => !passed && handleDayClick(day)}
          className={`relative min-h-24 border border-gray-200 p-2 transition
            ${passed ? 'bg-gray-100 cursor-not-allowed opacity-50' : isToday ? 'bg-blue-50 hover:bg-blue-100' : 'bg-white hover:bg-gray-50'}
            ${!passed ? 'cursor-pointer' : ''}
          `}
        >
          <div
            className={`text-sm font-semibold mb-2 ${passed
                ? 'text-gray-400'
                : isToday
                  ? 'text-blue-600'
                  : 'text-gray-700'
              }`}
          >
            {day}
          </div>

          {count > 0 && (
            <div className="absolute bottom-1 left-1 right-1 pointer-events-none">
              <div className={`${ limiteRaggiunto ? 'bg-orange-600' : 'bg-emerald-700'} text-white text-xs px-2 py-1 rounded-md truncate shadow-sm`}>
                {count} appuntamenti
              </div>
            </div>
          )}
        </div>
      );
    }

    return days;
  };

  if (sediLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Caricamento sedi...</div>
      </div>
    );
  }

  if (sedi.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Nessuna sede disponibile</div>
      </div>
    );
  }

  return (
    <div className='py-2'>
      <BreadCrumbsAppoUser />
      <h2 className="text-xl py-6 mt-4">Calendario Appuntamenti</h2>

      { (sediLoading || meseLoading) && <Loader /> }

      <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
        {/* Tab delle sedi */}
        <div className="flex gap-2 mb-6 border-b border-gray-200">
          {sedi.map((sede: any, index: number) => (
            <button
              key={sede.id}
              onClick={() => setSelectedSedeIndex(index)}
              className={`px-6 py-3 font-semibold transition ${selectedSedeIndex === index
                  ? 'border-b-2 border-emerald-800 text-emerald-800'
                  : 'text-gray-500 hover:text-gray-700'
                }`}
            >
              {sede.regione}
            </button>
          ))}
        </div>

        {/* Header calendario */}
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-3xl font-bold text-gray-800">
            {currentDate
              .toLocaleDateString('it-IT', {
                month: 'long',
                year: 'numeric'
              })
              .toUpperCase()}
          </h1>

          <div className="flex gap-2">
            <button
              onClick={handlePrevMonth}
              className="h-10 bg-emerald-800 text-white px-2 rounded-lg hover:bg-emerald-900 transition"
            >
              <ChevronLeftIcon className="w-6 h-6" />
            </button>

            <button
              onClick={() => setCurrentDate(new Date())}
              className="h-10 bg-emerald-800 text-white px-3 rounded-lg hover:bg-emerald-900 transition"
            >
              Oggi
            </button>

            <select
              value={currentDate.getMonth()}
              onChange={(e) =>
                setCurrentDate(
                  new Date(
                    currentDate.getFullYear(),
                    Number(e.target.value),
                    1
                  )
                )
              }
              className="px-4 py-2 border rounded-lg bg-white"
            >
              {[
                'Gennaio', 'Febbraio', 'Marzo', 'Aprile',
                'Maggio', 'Giugno', 'Luglio', 'Agosto',
                'Settembre', 'Ottobre', 'Novembre', 'Dicembre'
              ].map((mese, i) => (
                <option key={i} value={i}>
                  {mese}
                </option>
              ))}
            </select>

            <button
              onClick={handleNextMonth}
              className="h-10 bg-emerald-800 text-white px-2 rounded-lg hover:bg-emerald-900 transition"
            >
              <ChevronRightIcon className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Messaggio accesso negato */}
        {sediVisibiliData?.result?.[selectedSede?.chiave] === false ? (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
            <p className="text-yellow-800 font-semibold text-lg">
              Non puoi visualizzare gli appuntamenti di questa sede
            </p>
          </div>
        ) : (
          <>
            {meseLoading && (
              <div className="text-center text-gray-500 mb-4">
                Caricamento appuntamenti...
              </div>
            )}

            {/* Intestazioni giorni */}
            <div className="grid grid-cols-7 border border-gray-200 rounded-lg overflow-hidden">
              {['Dom', 'Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab'].map((d) => (
                <div
                  key={d}
                  className="bg-gray-100 p-3 text-center font-semibold text-sm"
                >
                  {d}
                </div>
              ))}
            </div>

            {/* Griglia calendario */}
            <div className="grid grid-cols-7 border border-t-0 border-gray-200">
              {renderCalendar()}
            </div>
          </>
        )}
      </div>

      {/* Dialog dettaglio giorno */}
      {showModal && selectedDay && selectedSede && (
        <AppointmentsDayDialog
          selectedDay={selectedDay}
          sedeNome={selectedSede.nome}
          sedeRegione={selectedSede.regione}
          onClose={() => setShowModal(false)}
        />
      )}
    </div>
  );
}
